/**
 * SettingsLayout — shared shell for the /crm/settings/* hub.
 *
 * Wraps the LIST/config pages (Studies, Forms, Flows, Global templates,
 * AI Prompts, Release wording, System) with a settings sub-nav: a sticky
 * vertical rail on lg+, a horizontal scroll row below lg. Detail/builder
 * pages (/settings/studies/:id, /settings/forms/:id, …) intentionally render
 * WITHOUT this wrapper — they are full-width work surfaces with their own
 * back links.
 *
 * Visibility mirrors the API gates: Templates / AI Prompts / Release wording
 * are CRM-admin-only; System is super-admin-only. Studies/Forms/Flows are
 * open to every CRM user (full-parity editing — see useCanEditForms).
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Activity,
  BookmarkPlus,
  CalendarClock,
  ClipboardList,
  DatabaseZap,
  FileSignature,
  FlaskConical,
  MessageSquareText,
  Sparkles,
  Tags,
  Workflow,
  ShieldCheck,
  Bot, Megaphone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useIsCRMAdmin } from '../../lib';

interface SettingsNavItem {
  to: string;
  icon: LucideIcon;
  label: string;
}

export default function SettingsLayout({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isCrmAdmin = useIsCRMAdmin();

  const items: SettingsNavItem[] = [
    { to: '/crm/settings/studies', icon: FlaskConical, label: 'Studies' },
    { to: '/crm/settings/forms', icon: ClipboardList, label: 'Forms' },
    { to: '/crm/settings/profile-keys', icon: Tags, label: 'Field tags' },
    { to: '/crm/settings/interest-lists', icon: BookmarkPlus, label: 'Future-study lists' },
    { to: '/crm/settings/flows', icon: Workflow, label: 'Flows' },
    ...(isCrmAdmin
      ? [
          { to: '/crm/settings/templates', icon: MessageSquareText, label: 'Global templates' } as SettingsNavItem,
          { to: '/crm/settings/prompts', icon: Sparkles, label: 'AI Prompts' } as SettingsNavItem,
          { to: '/crm/settings/release-content', icon: FileSignature, label: 'Release wording' } as SettingsNavItem,
          { to: '/crm/settings/registry-backfill', icon: DatabaseZap, label: 'Registry backfill' } as SettingsNavItem,
          { to: '/crm/settings/clinician-portal', icon: ShieldCheck, label: 'Clinician portal' } as SettingsNavItem,
          { to: '/crm/settings/agent', icon: Bot, label: 'Texting agent' } as SettingsNavItem,
          { to: '/crm/settings/booking', icon: CalendarClock, label: 'Booking hours' } as SettingsNavItem,
          { to: '/crm/settings/ad-campaigns', icon: Megaphone, label: 'Ad campaigns' } as SettingsNavItem,
        ]
      : []),
    ...(isSuperAdmin ? [{ to: '/crm/settings/system', icon: Activity, label: 'System' } as SettingsNavItem] : []),
  ];

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    `flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 lg:min-h-0 lg:py-1.5 ${
      isActive
        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/60 dark:hover:text-gray-100'
    }`;

  return (
    <div className="lg:grid lg:grid-cols-[11rem_1fr] lg:items-start lg:gap-8">
      <nav aria-label="CRM settings" className="mb-4 lg:sticky lg:top-16 lg:mb-0">
        <div className="mb-1 hidden px-2.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 lg:block">
          Settings
        </div>
        <div className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:pb-0">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} className={linkCls}>
              <it.icon className="h-4 w-4 flex-shrink-0" />
              {it.label}
            </NavLink>
          ))}
        </div>
      </nav>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
